// Auto-saving meeting notes hook.
//
// Loads form state from /api/data/meeting-notes for a given (prospectId, type)
// on mount, and PUT-saves it back debounced 1 second after the last change.
// Intended for the Discovery Call and Strategy Meeting forms — these stay open
// for an entire client meeting, so accidental nav/refresh must not lose data.
//
// Returns:
//   formData          — current form state
//   updateField(k, v) — set a single key
//   updateFields(obj) — set multiple keys at once (e.g. for nested updates)
//   setFormData(obj)  — replace entire state (used by reset)
//   savedAt           — Date of last successful save (or null)
//   saving            — true while a save is in flight
//   loadError         — error string if the initial load failed
//   saveError         — error string if a save failed (most recent)
//   loaded            — true once the initial load completes

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../utils/apiFetch';

const SAVE_DEBOUNCE_MS = 1000;

export function useMeetingNotes({ prospectId, type, defaultFormData }) {
  const [formData, setFormDataState] = useState(defaultFormData || {});
  const [loaded, setLoaded] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const saveTimer = useRef(null);
  const dirty = useRef(false);
  const inFlightVersion = useRef(0);
  const latestData = useRef(formData);

  // Load once on mount (or when prospectId/type changes)
  useEffect(() => {
    if (!prospectId || !type) return;
    let cancelled = false;
    setLoaded(false);
    setLoadError(null);
    apiFetch(`/api/data/meeting-notes?prospectId=${encodeURIComponent(prospectId)}&type=${type}`)
      .then(r => r.json())
      .then(j => {
        if (cancelled) return;
        if (j.error) throw new Error(j.error);
        const data = j.notes?.formData || defaultFormData || {};
        setFormDataState(data);
        latestData.current = data;
        if (j.notes?.updatedAt) setSavedAt(new Date(j.notes.updatedAt));
        setLoaded(true);
      })
      .catch(e => { if (!cancelled) { setLoadError(e.message); setLoaded(true); } });
    return () => {
      cancelled = true;
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospectId, type]);

  // Debounced save fires SAVE_DEBOUNCE_MS after the last call to scheduleSave().
  // Always sends the LATEST state captured in latestData.current.
  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    dirty.current = true;
    saveTimer.current = setTimeout(async () => {
      if (!dirty.current || !prospectId || !type) return;
      const myVersion = ++inFlightVersion.current;
      setSaving(true);
      setSaveError(null);
      const snapshot = latestData.current;
      try {
        const r = await apiFetch('/api/data/meeting-notes', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prospectId, type, formData: snapshot }),
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        // If a newer save kicked off while this one was in flight, ignore stale ack
        if (myVersion === inFlightVersion.current) {
          setSavedAt(new Date());
          dirty.current = false;
        }
      } catch (e) {
        setSaveError(e.message);
      } finally {
        if (myVersion === inFlightVersion.current) setSaving(false);
      }
    }, SAVE_DEBOUNCE_MS);
  }, [prospectId, type]);

  const updateField = useCallback((key, value) => {
    setFormDataState(prev => {
      const next = { ...prev, [key]: value };
      latestData.current = next;
      return next;
    });
    scheduleSave();
  }, [scheduleSave]);

  const updateFields = useCallback((patch) => {
    setFormDataState(prev => {
      const next = { ...prev, ...patch };
      latestData.current = next;
      return next;
    });
    scheduleSave();
  }, [scheduleSave]);

  const setFormData = useCallback((next) => {
    setFormDataState(next);
    latestData.current = next;
    scheduleSave();
  }, [scheduleSave]);

  // Save-on-unmount safety net: if the user navigates away while a debounced
  // save is still pending, fire it synchronously so we don't lose the last
  // few seconds of edits.
  useEffect(() => {
    return () => {
      if (dirty.current && prospectId && type && saveTimer.current) {
        clearTimeout(saveTimer.current);
        const snapshot = latestData.current;
        // Best effort — fire and forget. Browser may abort during nav.
        try {
          navigator.sendBeacon?.(
            '/api/data/meeting-notes',
            new Blob([JSON.stringify({ prospectId, type, formData: snapshot })], { type: 'application/json' })
          );
        } catch (e) { /* ignore */ }
      }
    };
  }, [prospectId, type]);

  return {
    formData, updateField, updateFields, setFormData,
    loaded, savedAt, saving, loadError, saveError,
  };
}
